// Lenco Pay widget helper.
//
// Two things to know about pay.lenco.co/js/v1/inline.js that shaped this file:
//
//   1) `LencoPay.getPaid()` delegates to a class method with a singleton guard
//      (`this.instance || (this.instance = new n(i))`). If the widget errors
//      internally, it removes its iframes but does NOT reset `this.instance`,
//      so every subsequent `getPaid()` call becomes a silent no-op. There is
//      no public reset API, and the only guaranteed way to clear the singleton
//      is to reload the script itself. So `loadScriptFresh()` deletes
//      `window.LencoPay`, drops the old <script>, and reloads on every launch.
//
//   2) The widget only accepts `onSuccess`, `onClose`, `onConfirmationPending`
//      callbacks. `onError` is ignored. Config that works matches the
//      LencoPay WooCommerce plugin's reference shape.

let loadingPromise = null;

function loadScriptFresh(url) {
  try { delete window.LencoPay; } catch (_) { window.LencoPay = undefined; }
  document.querySelectorAll('script[data-lenco-widget="1"]').forEach(s => s.remove());
  loadingPromise = new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = url + (url.includes('?') ? '&' : '?') + 't=' + Date.now();
    s.async = true;
    s.setAttribute('data-lenco-widget', '1');
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('Failed to load Lenco widget script'));
    document.body.appendChild(s);
  });
  return loadingPromise;
}

/**
 * Launch the widget. Fires `onSuccess`/`onConfirmationPending` when the
 * customer completes payment (both trigger our server-side verify) and
 * `onClose` when the modal is dismissed for any reason.
 *
 * @param {object} args
 * @param {string} args.publicKey
 * @param {string} args.scriptUrl
 * @param {string} args.reference
 * @param {number} args.amount
 * @param {string} args.email
 * @param {(response: any) => void} args.onSuccess
 * @param {() => void} [args.onClose]
 * @param {() => void} [args.onConfirmationPending]
 */
export async function launchLencoWidget(args) {
  await loadScriptFresh(args.scriptUrl || 'https://pay.lenco.co/js/v1/inline.js');
  // v1.9.29 — <script>.onload fires when the network response arrives, but
  // on mobile Chrome the script's JS body can still be parsing/evaluating
  // for another ~50–500 ms after that. If we check window.LencoPay
  // immediately (as we used to), the very first retry after a cancel
  // consistently throws "widget failed to initialize" because we caught the
  // script mid-evaluation. Poll every 50 ms for up to 2 s before giving up.
  const deadline = Date.now() + 2000;
  while ((!window.LencoPay || typeof window.LencoPay.getPaid !== 'function') && Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 50));
  }
  if (!window.LencoPay || typeof window.LencoPay.getPaid !== 'function') {
    throw new Error('Lenco widget failed to initialize');
  }
  // v1.8.0 — pre-fill customer.phone so the widget can skip its phone-entry
  // step when the member already has a phone on file. Only include the
  // customer block if we actually have a phone; otherwise omit it entirely
  // (the widget then shows its own input).
  const config = {
    key: args.publicKey,
    reference: args.reference,
    email: args.email,
    amount: Number(args.amount),
    currency: 'ZMW',
    channels: ['mobile-money'],
    onSuccess: (response) => { args.onSuccess?.(response); },
    onConfirmationPending: () => { args.onConfirmationPending?.(); },
    onClose:   ()         => { args.onClose?.(); },
  };
  if (args.phone) {
    config.customer = {
      firstName: args.firstName || '',
      lastName:  args.lastName  || '',
      phone:     args.phone,
    };
  }
  window.LencoPay.getPaid(config);
}
